/**
 * 1stbuddha — toast.js
 *
 * <toast-region> (snippets/toast-region.liquid) listens for `toast:show`
 * events on document and stacks toasts built from its <template>.
 *
 *   document.dispatchEvent(new CustomEvent('toast:show', {
 *     detail: {
 *       message: 'Saved to your wishlist',          // required, plain text
 *       variant: 'error',                           // optional
 *       action: { label: 'View', href: '/pages/wishlist' }, // optional link
 *       // or action: { label: 'Retry', onClick: () => {} }
 *     }
 *   }));
 *
 * Spec: auto-dismiss after --duration-toast (4 s), paused while hovered or
 * focused; max 2 stacked; errors use role="alert".
 */
(() => {
  if (customElements.get('toast-region')) return;

  const MAX_TOASTS = 2;

  const readDuration = (token, fallback) => {
    const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(token));
    return Number.isNaN(value) ? fallback : value;
  };

  class ToastRegion extends HTMLElement {
    constructor() {
      super();
      this.onShow = (event) => this.show(event.detail || {});
    }

    connectedCallback() {
      this.template = this.querySelector('template');
      document.addEventListener('toast:show', this.onShow);
    }

    disconnectedCallback() {
      document.removeEventListener('toast:show', this.onShow);
    }

    show({ message, variant, action }) {
      if (!message || !this.template) return null;

      const toast = this.template.content.firstElementChild.cloneNode(true);
      toast.querySelector('.toast__message').textContent = message;

      if (variant === 'error') {
        toast.classList.add('toast--error');
        toast.setAttribute('role', 'alert');
      }

      if (action?.label) {
        const actionEl = toast.querySelector('.toast__action');
        actionEl.textContent = action.label;
        actionEl.hidden = false;
        if (action.href) {
          actionEl.href = action.href;
        } else {
          actionEl.setAttribute('role', 'button');
          actionEl.href = '#';
          actionEl.addEventListener('click', (event) => {
            event.preventDefault();
            action.onClick?.();
            this.dismiss(toast);
          });
        }
      }

      toast.querySelector('.toast__close').addEventListener('click', () => this.dismiss(toast));
      this.bindTimer(toast);

      this.append(toast);
      const toasts = this.querySelectorAll('.toast:not(.is-leaving)');
      if (toasts.length > MAX_TOASTS) this.dismiss(toasts[0]);

      return toast;
    }

    bindTimer(toast) {
      const duration = readDuration('--duration-toast', 4000);
      const start = () => {
        clearTimeout(toast.timer);
        toast.timer = setTimeout(() => this.dismiss(toast), duration);
      };
      const pause = () => clearTimeout(toast.timer);

      toast.addEventListener('mouseenter', pause);
      toast.addEventListener('mouseleave', start);
      toast.addEventListener('focusin', pause);
      toast.addEventListener('focusout', (event) => {
        if (!toast.contains(event.relatedTarget)) start();
      });
      start();
    }

    dismiss(toast) {
      if (!toast || toast.classList.contains('is-leaving')) return;
      clearTimeout(toast.timer);
      toast.classList.add('is-leaving');

      const remove = () => toast.remove();
      toast.addEventListener('animationend', remove, { once: true });
      setTimeout(remove, readDuration('--duration-fast', 150) + 60);
    }
  }

  customElements.define('toast-region', ToastRegion);
})();
